import * as React from "react";
import { Loader2, Sparkles, UserRound } from "lucide-react";
import {
  Button,
  Input,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Switch,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { useStaffRoster } from "@/hooks/useStaff";
import {
  APPROVAL_MODE_LABELS,
  CADENCE_LABELS,
  PAY_BASIS_LABELS,
  WEEKDAY_SHORT,
  paiseToRupees,
  rupeesToPaise,
  useAssistTerms,
  useCreateTeamMember,
  useUpdateTeamMember,
  type ApprovalMode,
  type PayBasis,
  type PaymentCadence,
  type TeamMember,
  type TermsInput,
} from "@/lib/teamPay/api";
import { ErrorBanner, Field, NativeSelect, PreviewNote } from "@/components/team-pay/shared";
import { errorMessage, useTeamWords } from "@/lib/teamPay/words";

/**
 * Phase SC-22d — add or edit a team member with their employment terms.
 * "Describe the arrangement" turns plain words into terms (AI with a
 * rules fallback, BE side); nothing is saved until the owner confirms.
 */

interface Draft {
  name: string;
  roleLabel: string;
  linkedUserId: string;
  basis: PayBasis;
  rateRupees: string;
  hoursPerDay: string;
  weeklyOffs: number[];
  leavePerMonth: string;
  leavePerYear: string;
  joinedOn: string;
  exitedOn: string;
  cadence: PaymentCadence;
  mode: ApprovalMode | "__default__";
  isActive: boolean;
  note: string;
}

const NO_LINK = "__none__";

function emptyDraft(): Draft {
  return {
    name: "",
    roleLabel: "",
    linkedUserId: NO_LINK,
    basis: "monthly",
    rateRupees: "",
    hoursPerDay: "",
    weeklyOffs: [6],
    leavePerMonth: "0",
    leavePerYear: "0",
    joinedOn: "",
    exitedOn: "",
    cadence: "monthly",
    mode: "__default__",
    isActive: true,
    note: "",
  };
}

function rateFor(terms: TermsInput | TeamMember["terms"], monthly: number | null | undefined) {
  if (terms.pay_basis === "daily") return terms.daily_rate_paise ?? null;
  if (terms.pay_basis === "hourly") return terms.hourly_rate_paise ?? null;
  return monthly ?? null;
}

function draftFromMember(m: TeamMember | null): Draft {
  if (!m) return emptyDraft();
  const rate = rateFor(m.terms, m.monthly_salary_paise);
  return {
    name: m.name,
    roleLabel: m.role_label,
    linkedUserId: m.linked_user_id ?? NO_LINK,
    basis: m.terms.pay_basis,
    rateRupees: rate !== null ? String(paiseToRupees(rate)) : "",
    hoursPerDay: m.terms.expected_hours_per_day ? String(m.terms.expected_hours_per_day) : "",
    weeklyOffs: m.terms.weekly_off_days,
    leavePerMonth: String(m.terms.paid_leave_per_month),
    leavePerYear: String(m.terms.paid_leave_per_year),
    joinedOn: m.terms.joined_on ?? "",
    exitedOn: m.terms.exited_on ?? "",
    cadence: m.terms.payment_cadence,
    mode: m.terms.attendance_mode ?? "__default__",
    isActive: m.is_active,
    note: m.note ?? "",
  };
}

function applySuggestion(draft: Draft, terms: TermsInput): Draft {
  const rate = rateFor(terms, terms.monthly_salary_paise);
  return {
    ...draft,
    basis: terms.pay_basis,
    rateRupees: rate !== null ? String(paiseToRupees(rate)) : draft.rateRupees,
    hoursPerDay: terms.expected_hours_per_day
      ? String(terms.expected_hours_per_day)
      : draft.hoursPerDay,
    weeklyOffs: terms.weekly_off_days,
    leavePerMonth: String(terms.paid_leave_per_month),
    leavePerYear: String(terms.paid_leave_per_year),
    joinedOn: terms.joined_on ?? draft.joinedOn,
    exitedOn: terms.exited_on ?? draft.exitedOn,
    cadence: terms.payment_cadence,
  };
}

function toNumber(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

const RATE_LABEL: Record<PayBasis, string> = {
  monthly: "Monthly salary (₹)",
  daily: "Pay per day (₹)",
  hourly: "Pay per hour (₹)",
};

export function MemberTermsSheet({
  open,
  onOpenChange,
  member,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: TeamMember | null;
}) {
  const words = useTeamWords();
  const [draft, setDraft] = React.useState<Draft>(() => draftFromMember(member));
  const [describe, setDescribe] = React.useState("");
  const [assistNote, setAssistNote] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const create = useCreateTeamMember();
  const update = useUpdateTeamMember();
  const assist = useAssistTerms();
  const roster = useStaffRoster({ limit: 200 });
  const isEdit = member !== null;
  const busy = create.isPending || update.isPending;

  React.useEffect(() => {
    if (open) {
      setDraft(draftFromMember(member));
      setDescribe("");
      setAssistNote(null);
      setError(null);
    }
  }, [open, member]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const linkable = React.useMemo(
    () =>
      (roster.data?.items ?? []).filter(
        (u) => u.role === "staff" || u.role === "teacher" || u.role === "admin",
      ),
    [roster.data],
  );

  const ratePaise = rupeesToPaise(draft.rateRupees);
  const canSubmit =
    draft.name.trim().length > 0 &&
    draft.roleLabel.trim().length > 0 &&
    ratePaise !== null &&
    ratePaise > 0 &&
    !busy;

  const runAssist = async () => {
    setAssistNote(null);
    setError(null);
    try {
      const result = await assist.mutateAsync(describe.trim());
      setDraft((prev) => applySuggestion(prev, result.terms));
      setAssistNote(
        [result.rationale, ...result.notes].join(" ") + " Check the fields below before saving.",
      );
    } catch (err) {
      setError(errorMessage(err, "Couldn't read that description. Fill the fields instead."));
    }
  };

  const toggleOff = (day: number) =>
    set(
      "weeklyOffs",
      draft.weeklyOffs.includes(day)
        ? draft.weeklyOffs.filter((d) => d !== day)
        : [...draft.weeklyOffs, day].sort(),
    );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || ratePaise === null) return;
    setError(null);
    const terms: TermsInput = {
      pay_basis: draft.basis,
      monthly_salary_paise: draft.basis === "monthly" ? ratePaise : null,
      daily_rate_paise: draft.basis === "daily" ? ratePaise : null,
      hourly_rate_paise: draft.basis === "hourly" ? ratePaise : null,
      expected_hours_per_day:
        draft.basis === "hourly" && draft.hoursPerDay ? toNumber(draft.hoursPerDay) : null,
      weekly_off_days: draft.weeklyOffs,
      paid_leave_per_month: toNumber(draft.leavePerMonth),
      paid_leave_per_year: toNumber(draft.leavePerYear),
      joined_on: draft.joinedOn || null,
      exited_on: draft.exitedOn || null,
      payment_cadence: draft.cadence,
      attendance_mode: draft.mode === "__default__" ? null : draft.mode,
    };
    const linked = draft.linkedUserId === NO_LINK ? null : draft.linkedUserId;
    try {
      if (member) {
        await update.mutateAsync({
          id: member.id,
          body: {
            name: draft.name.trim(),
            role_label: draft.roleLabel.trim(),
            linked_user_id: linked,
            is_active: draft.isActive,
            note: draft.note.trim() || null,
            terms,
          },
        });
        toast.success(`Saved ${draft.name.trim()}.`);
      } else {
        await create.mutateAsync({
          name: draft.name.trim(),
          role_label: draft.roleLabel.trim(),
          linked_user_id: linked,
          note: draft.note.trim() || null,
          terms,
        });
        toast.success(`Added ${draft.name.trim()}.`);
      }
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err, "Couldn't save. Please retry."));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[520px]"
        data-testid="member-terms-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <UserRound className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">
                {isEdit ? `Edit ${member.name}` : `Add ${words.member.toLowerCase()}`}
              </SheetTitle>
              <SheetDescription className="mt-1 text-xs">
                How they are paid, their days off and leave. Pay is worked out from these terms and
                their attendance.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {error ? <ErrorBanner message={error} /> : null}

            <section className="border-primary/20 bg-primary/[0.03] space-y-2 rounded-xl border p-3.5">
              <label
                htmlFor="tp-describe"
                className="flex items-center gap-1.5 text-sm font-medium"
              >
                <Sparkles aria-hidden="true" className="text-primary size-4" />
                Describe the arrangement
              </label>
              <Textarea
                id="tp-describe"
                value={describe}
                rows={2}
                maxLength={2000}
                onChange={(e) => setDescribe(e.target.value)}
                placeholder="₹600 a day, Sunday off, one paid leave a month, joined 5 Oct"
              />
              <div className="flex items-center justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={describe.trim().length < 3 || assist.isPending}
                  onClick={runAssist}
                >
                  {assist.isPending ? (
                    <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  ) : (
                    <Sparkles aria-hidden="true" className="size-4" />
                  )}
                  Fill the terms
                </Button>
              </div>
              {assistNote ? <PreviewNote>{assistNote}</PreviewNote> : null}
            </section>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name" htmlFor="tp-name" required>
                <Input
                  id="tp-name"
                  value={draft.name}
                  maxLength={200}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Ramesh Kumar"
                />
              </Field>
              <Field label="Role" htmlFor="tp-role" required>
                <Input
                  id="tp-role"
                  value={draft.roleLabel}
                  maxLength={80}
                  onChange={(e) => set("roleLabel", e.target.value)}
                  placeholder="Front desk"
                />
              </Field>
            </div>

            <Field
              label="Portal account"
              htmlFor="tp-link"
              helper="Linked members can check in, see their pay and ask for leave from the portal."
            >
              <NativeSelect
                id="tp-link"
                value={draft.linkedUserId}
                onChange={(e) => set("linkedUserId", e.target.value)}
              >
                <option value={NO_LINK}>No portal account</option>
                {linkable.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name ?? u.email} · {u.email}
                  </option>
                ))}
              </NativeSelect>
            </Field>

            <fieldset className="space-y-3">
              <legend className="text-sm font-medium">Pay</legend>
              <div
                role="radiogroup"
                aria-label="Pay basis"
                className="bg-muted/40 grid grid-cols-3 gap-1 rounded-lg p-1"
              >
                {(Object.keys(PAY_BASIS_LABELS) as PayBasis[]).map((b) => (
                  <button
                    key={b}
                    type="button"
                    role="radio"
                    aria-checked={draft.basis === b}
                    onClick={() => set("basis", b)}
                    className={cn(
                      "rounded-md px-2 py-1.5 text-xs font-medium transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.97]",
                      draft.basis === b
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {PAY_BASIS_LABELS[b]}
                  </button>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={RATE_LABEL[draft.basis]} htmlFor="tp-rate" required>
                  <Input
                    id="tp-rate"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={draft.rateRupees}
                    onChange={(e) => set("rateRupees", e.target.value)}
                  />
                </Field>
                {draft.basis === "hourly" ? (
                  <Field label="Usual hours a day" htmlFor="tp-hours">
                    <Input
                      id="tp-hours"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={24}
                      step="0.5"
                      value={draft.hoursPerDay}
                      onChange={(e) => set("hoursPerDay", e.target.value)}
                    />
                  </Field>
                ) : (
                  <Field label="Paid" htmlFor="tp-cadence">
                    <NativeSelect
                      id="tp-cadence"
                      value={draft.cadence}
                      onChange={(e) => set("cadence", e.target.value as PaymentCadence)}
                    >
                      {(Object.keys(CADENCE_LABELS) as PaymentCadence[]).map((c) => (
                        <option key={c} value={c}>
                          {CADENCE_LABELS[c]}
                        </option>
                      ))}
                    </NativeSelect>
                  </Field>
                )}
              </div>
            </fieldset>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Weekly days off</legend>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAY_SHORT.map((label, idx) => {
                  const on = draft.weeklyOffs.includes(idx);
                  return (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleOff(idx)}
                      className={cn(
                        "h-8 min-w-11 rounded-full border px-3 text-xs font-medium transition-[background-color,border-color,color,transform] duration-150 ease-out active:scale-[0.97]",
                        on
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {draft.basis !== "hourly" ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Paid leave a month" htmlFor="tp-lpm">
                  <Input
                    id="tp-lpm"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.5"
                    value={draft.leavePerMonth}
                    onChange={(e) => set("leavePerMonth", e.target.value)}
                  />
                </Field>
                <Field label="Paid leave a year" htmlFor="tp-lpy">
                  <Input
                    id="tp-lpy"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.5"
                    value={draft.leavePerYear}
                    onChange={(e) => set("leavePerYear", e.target.value)}
                  />
                </Field>
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Joined on" htmlFor="tp-joined">
                <Input
                  id="tp-joined"
                  type="date"
                  value={draft.joinedOn}
                  onChange={(e) => set("joinedOn", e.target.value)}
                />
              </Field>
              <Field label="Last day" htmlFor="tp-exited">
                <Input
                  id="tp-exited"
                  type="date"
                  value={draft.exitedOn}
                  min={draft.joinedOn || undefined}
                  onChange={(e) => set("exitedOn", e.target.value)}
                />
              </Field>
            </div>

            <Field label="Attendance approval" htmlFor="tp-mode">
              <NativeSelect
                id="tp-mode"
                value={draft.mode}
                onChange={(e) => set("mode", e.target.value as Draft["mode"])}
              >
                <option value="__default__">Use the centre setting</option>
                {(Object.keys(APPROVAL_MODE_LABELS) as ApprovalMode[]).map((m) => (
                  <option key={m} value={m}>
                    {APPROVAL_MODE_LABELS[m]}
                  </option>
                ))}
              </NativeSelect>
            </Field>

            {isEdit ? (
              <label className="border-border/60 bg-muted/20 flex items-center gap-3 rounded-md border px-3 py-2">
                <Switch
                  checked={draft.isActive}
                  onCheckedChange={(v) => set("isActive", v)}
                  aria-label="Active"
                />
                <span className="text-sm font-medium">Active</span>
              </label>
            ) : null}

            <Field label="Note" htmlFor="tp-note">
              <Textarea
                id="tp-note"
                rows={2}
                maxLength={2000}
                value={draft.note}
                onChange={(e) => set("note", e.target.value)}
              />
            </Field>
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={busy}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={!canSubmit}>
              {busy ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
              {isEdit ? "Save terms" : `Add ${words.member.toLowerCase()}`}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
